import React, { useState, useEffect } from 'react';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  Download,
  Eye,
  Search,
  Sparkles,
  FileOutput
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import api from '../lib/api';

const DOCUMENT_CATEGORIES = [
  { value: 'contract', label: 'Contract' },
  { value: 'letter', label: 'Letter' },
  { value: 'court_filing', label: 'Court Filing' },
  { value: 'agreement', label: 'Agreement' },
  { value: 'notice', label: 'Notice' },
  { value: 'other', label: 'Other' },
];

export const DocumentTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [generateTemplate, setGenerateTemplate] = useState(null);
  const [variableValues, setVariableValues] = useState({});
  const [generateCase, setGenerateCase] = useState('');
  const [generateClient, setGenerateClient] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'letter',
    content: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, casesRes, clientsRes] = await Promise.all([
        api.get('/document-templates'),
        api.get('/cases'),
        api.get('/clients'),
      ]);
      setTemplates(templatesRes.data);
      setCases(casesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const response = await api.get('/document-templates/defaults/seed');
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to create default templates');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await api.put(`/document-templates/${editingTemplate.template_id}`, formData);
        toast.success('Template updated');
      } else {
        await api.post('/document-templates', formData);
        toast.success('Template created');
      }
      fetchData();
      resetForm();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleGenerate = async () => {
    if (!generateTemplate) return;
    try {
      const response = await api.post(`/document-templates/${generateTemplate.template_id}/generate`, {
        template_id: generateTemplate.template_id,
        variables: variableValues,
        case_id: generateCase || null,
        client_id: generateClient || null,
        output_format: 'pdf',
      });
      toast.success('Document generated! Check your documents.');
      setIsGenerateOpen(false);
      setGenerateTemplate(null);
      setVariableValues({});
    } catch (error) {
      toast.error('Failed to generate document');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/document-templates/${deleteId}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    } finally {
      setDeleteId(null);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      category: template.category,
      content: template.content,
    });
    setIsDialogOpen(true);
  };

  const openGenerateDialog = (template) => {
    setGenerateTemplate(template);
    const initialValues = {};
    (template.variables || []).forEach(v => {
      initialValues[v] = '';
    });
    setVariableValues(initialValues);
    setIsGenerateOpen(true);
  };

  const resetForm = () => {
    setFormData({ name: '', category: 'letter', content: '' });
    setEditingTemplate(null);
    setIsDialogOpen(false);
  };

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getCategoryColor = (category) => {
    const colors = {
      contract: 'bg-blue-100 text-blue-700',
      letter: 'bg-green-100 text-green-700',
      court_filing: 'bg-purple-100 text-purple-700',
      agreement: 'bg-amber-100 text-amber-700',
      notice: 'bg-red-100 text-red-700',
      other: 'bg-slate-100 text-slate-700',
    };
    return colors[category] || colors.other;
  };

  if (loading) {
    return <PageLoader text="Loading document templates..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="document-templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Document Templates</h1>
          <p className="text-slate-500 mt-1">Create and generate legal documents from templates</p>
        </div>
        <div className="flex items-center gap-2">
          {templates.length === 0 && (
            <Button variant="outline" onClick={handleSeedDefaults} className="gap-2">
              <Sparkles className="h-4 w-4" />
              Add Defaults
            </Button>
          )}
          <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-doc-template-btn">
                <Plus className="h-4 w-4" />
                Create Template
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Document Template'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Template Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="Engagement Letter"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DOCUMENT_CATEGORIES.map(c => (
                          <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="content">Document Content (HTML supported) *</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    required
                    rows={15}
                    placeholder="<h1>Document Title</h1><p>Dear {{client_name}},</p>..."
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-slate-500">
                    Use {'{{variable_name}}'} for placeholders. Common variables: client_name, date, firm_name, attorney_name, case_title
                  </p>
                </div>
                
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit">{editingTemplate ? 'Update' : 'Create'} Template</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Tabs value={categoryFilter} onValueChange={setCategoryFilter}>
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="letter">Letters</TabsTrigger>
            <TabsTrigger value="contract">Contracts</TabsTrigger>
            <TabsTrigger value="court_filing">Filings</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <Card key={template.template_id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{template.name}</CardTitle>
                    <Badge className={`mt-2 ${getCategoryColor(template.category)}`}>
                      {DOCUMENT_CATEGORIES.find(c => c.value === template.category)?.label}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openGenerateDialog(template)} className="h-8 w-8 p-0 text-green-600">
                      <FileOutput className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setPreviewTemplate(template)} className="h-8 w-8 p-0">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(template)} className="h-8 w-8 p-0">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(template.template_id)} className="h-8 w-8 p-0 text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {template.variables && template.variables.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {template.variables.slice(0, 4).map((v, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {`{{${v}}}`}
                      </Badge>
                    ))}
                    {template.variables.length > 4 && (
                      <Badge variant="outline" className="text-xs">+{template.variables.length - 4}</Badge>
                    )}
                  </div>
                )}
                <p className="text-xs text-slate-400">
                  Updated {formatDate(template.updated_at)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No templates found</h3>
            <p className="text-slate-500 mb-4">Create document templates to generate legal documents quickly</p>
            {templates.length === 0 && (
              <Button onClick={handleSeedDefaults} className="gap-2">
                <Sparkles className="h-4 w-4" />
                Add Default Templates
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview: {previewTemplate?.name}</DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="mt-4">
              <div 
                className="prose prose-sm max-w-none p-4 border rounded-lg bg-white"
                dangerouslySetInnerHTML={{ __html: previewTemplate.content }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Generate Document Dialog */}
      <Dialog open={isGenerateOpen} onOpenChange={() => { setIsGenerateOpen(false); setGenerateTemplate(null); }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Generate Document: {generateTemplate?.name}</DialogTitle>
          </DialogHeader>
          {generateTemplate && (
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-500">Fill in the variables to generate your document</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Link to Case (optional)</Label>
                  <Select value={generateCase} onValueChange={setGenerateCase}>
                    <SelectTrigger><SelectValue placeholder="Select case" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {cases.map(c => (
                        <SelectItem key={c.case_id} value={c.case_id}>{c.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Link to Client (optional)</Label>
                  <Select value={generateClient} onValueChange={setGenerateClient}>
                    <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {generateTemplate.variables && generateTemplate.variables.length > 0 && (
                <div className="space-y-3">
                  <Label>Template Variables</Label>
                  {generateTemplate.variables.map((v) => (
                    <div key={v} className="space-y-1">
                      <Label className="text-sm text-slate-600">{v.replace(/_/g, ' ')}</Label>
                      <Input
                        value={variableValues[v] || ''}
                        onChange={(e) => setVariableValues({ ...variableValues, [v]: e.target.value })}
                        placeholder={`Enter ${v.replace(/_/g, ' ')}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => { setIsGenerateOpen(false); setGenerateTemplate(null); }}>
                  Cancel
                </Button>
                <Button onClick={handleGenerate} className="gap-2">
                  <Download className="h-4 w-4" />
                  Generate PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DocumentTemplates;
